import { CTA, Spinner, styled, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { getDatosEconomicos, isOfertaV2 } from 'src/bff';
import { Apellido, CarlineSelect, CodigoPostal, DniNif, Email, InputGroupWrapper, InputWrapper, Nombre, RadioButtonGroup, SuggestInstallation, Telefono, Trato } from 'src/components';
import { getTrackingForm, LayoutWrapper, useOneFormContext, useTrackingManager } from 'src/feature-app';
import { Info } from 'src/icons-core-imports';
import { DatoEconomicoOffer, OneFormState } from 'src/types';

const Section = styled.div`
	padding: 44px;
	border: 1px solid #cccccc;
	margin: 20px -44px;
`;

export function CallCenterComercialesScreen() {
	const { formData } = useSelector((state: OneFormState) => state);
	const { fields } = formData;
	const [isArgumentado, setIsArgumentado] = useState(true);

	useEffect(() => {
		setIsArgumentado(!fields.estadoLlamada || fields.estadoLlamada === 'Argumentado');
	}, [fields.estadoLlamada]);

	const optionsEstadoLlamada = [
		{
			label: 'Argumentado',
			value: 'Argumentado',
		},
		{
			label: 'No argumentado',
			value: 'No argumentado',
		},
		{
			label: 'Solo quiere info del Call Center',
			value: 'Solo quiere info del Call Center',
		},
		{
			label: 'Ilocalizable',
			value: 'Ilocalizable',
		},
	];

	return (
		<LayoutWrapper>
			<Text appearance={TokenTextAppearance.headline500}>Call me now</Text>
			<Section>
				<Text bold>Estado de la llamada</Text>
				<RadioButtonGroup options={optionsEstadoLlamada} name="estadoLlamada" required />
			</Section>
			<Section>
				<Text bold>Datos personales</Text>
				<InputGroupWrapper className="datos_personales-group-wrapper" paddingTop="15px">
					<Trato />
					<Nombre />
					<Apellido name="surname" label="Primer apellido" required />
					<Apellido name="secondSurname" label="Segundo apellido" />
					<DniNif />
					<CodigoPostal showProvince required={isArgumentado} />
				</InputGroupWrapper>
			</Section>
			<Section>
				<Text bold>Datos de contacto</Text>
				<InputGroupWrapper className="datos_contacto-group-wrapper" paddingTop="15px">
					<Telefono required />
					<Email required />
				</InputGroupWrapper>
				<InputGroupWrapper className="consentimientos-group-wrapper">
					<InputWrapper>
						<Text>
							Comunicaciones comerciales: <Text bold>{fields.lssi}</Text>
						</Text>
					</InputWrapper>
					<InputWrapper>
						<Text>
							Perfilado: <Text bold>{fields.tmk}</Text>
						</Text>
					</InputWrapper>
				</InputGroupWrapper>
			</Section>
			<Section>
				<Text bold>Datos del vehículo y instalación</Text>
				<InputGroupWrapper className="instalacion_vehiculo-group-wrapper">
					<InputWrapper>
						<CarlineOrOferta />
					</InputWrapper>
					<InputWrapper>
						<SuggestInstallation dpsDomain={'LCV_SF_LEAD_INSTALLATION'} dpsToken={'LCV_DDB_FRM_LEM_REC_LEM'} required={isArgumentado} paddingTop={'0'} paddingBottom={'24px'} />
						<InstallationInfo />
					</InputWrapper>
				</InputGroupWrapper>
			</Section>
			<SendComponent />
		</LayoutWrapper>
	);
}

const InstallationWrapper = styled.div`
	min-height: 120px;
`;

const AlertSuccess = styled.div`
	width: 100%;
	padding: 1rem;
	margin: 1rem 0;
	border: 1px solid transparent;
	border-radius: 0.375rem;
	color: #0f5132;
	background-color: #d1e7dd;
	border-color: #badbcc;
`;

const AlertError = styled.div`
	width: 100%;
	padding: 1rem;
	margin: 1rem 0;
	border: 1px solid transparent;
	border-radius: 0.375rem;
	color: #842029;
	background-color: #f8d7da;
	border-color: #f5c2c7;
`;

const AlertInfo = styled.div`
	width: 100%;
	padding: 1rem;
	margin: 1rem 0;
	border: 1px solid transparent;
	border-radius: 0.375rem;
	color: #084298;
	background-color: #cfe2ff;
	border-color: #b6d4fe;
`;

function InstallationInfo() {
	const { dealersInfo } = useSelector((state: OneFormState) => state);
	const { selectedInstallation: installation } = dealersInfo;

	return (
		<InstallationWrapper>
			{installation ? (
				<>
					<Text bold>{installation.VGED_CommercialName__c}</Text>
					<Text appearance={TokenTextAppearance.copy100}>
						{installation.KVPSCode__c} - {installation.VGED_DealerCode__c} - {installation.VGED_ConcessionDealerCode__c}
					</Text>
					<Text>{installation.ShippingStreet}</Text>
					<Text>
						{installation.ShippingPostalCode} {installation.ShippingCity}
					</Text>
				</>
			) : null}
		</InstallationWrapper>
	);
}

function CarlineOrOferta() {
	const { dealersInfo } = useSelector((state: OneFormState) => state);
	const { oferta } = dealersInfo;

	if (!oferta) return <CarlineSelect useCarline={true} paddingTop="0" paddingBottom="24px" />;

	if (isOfertaV2(oferta)) {
		const datosEconomicos = getDatosEconomicos(oferta);
		// Este debera ser el unico return cuando ofertasV2 este publicado
		return datosEconomicos && datosEconomicos.length > 0 ? (
			<>
				<Text bold>OFERTA</Text>
				<Text>
					{oferta.carline.name.toUpperCase()} {oferta.configuracion.motorizacion?.name}
				</Text>

				{datosEconomicos.map((datoEconomico: DatoEconomicoOffer, index: number) => (
					<div key={index}>
						<Text appearance={TokenTextAppearance.copy100}>
							<Info variant="small" /> {datoEconomico.modalidad} {datoEconomico.precio}€{' '}
							{datoEconomico.periodicidad !== 'pago-unico' ? (
								<>
									{datoEconomico.periodicidad} {datoEconomico.plazos} plazos con {datoEconomico.tipoFinanciacion?.replace('-', ' ')} TIN {datoEconomico.tin || '-'}% TAE {datoEconomico.tae || '-'}%
								</>
							) : (
								'(pago único)'
							)}
						</Text>
					</div>
				))}
			</>
		) : null;
	} else {
		return (
			<>
				<Text bold>OFERTA</Text>
				<Text>
					{oferta.carline.name.toUpperCase()} {oferta.motorizacion}
				</Text>

				<Text appearance={TokenTextAppearance.copy100}>
					<Info variant="small" />
					{oferta.precio ? (
						<>
							{oferta.precioModalidad} {oferta.precio}€ (pago único)
						</>
					) : (
						<>
							{oferta.cuotaModalidad} {oferta.cuota}€ {oferta.cuotaModalidad} {oferta.plazos} plazos con {oferta.financiacionText} TIN {oferta.tin}% TAE {oferta.tae}%
						</>
					)}
				</Text>
			</>
		);
	}
}

function SendComponent() {
	const { handleSubmit } = useOneFormContext();
	const { getValues, triggerValidation } = useFormContext();
	//const { formData, dealersInfo }: OneFormState = store.getState();
	//const { fields, flags, headers } = formData;
	const dispatch = useDispatch();
	const trackingManager = useTrackingManager();

	const [sending, setSending] = useState(false);
	const [successAlert, setSuccessAlert] = useState(false);
	const [errorAlert, setErrorAlert] = useState(false);
	const [infoAlert, setInfoAlert] = useState(false);

	const closeAlerts = () => {
		setSuccessAlert(false);
		setErrorAlert(false);
		setInfoAlert(false);
	};

	async function handleClickSend(e: any) {
		setSending(true);
		closeAlerts();
		trackingManager.trackFormCTAClick({
			contentId: 'Enviar',
		});

		const isValid = await triggerValidation();

		if (isValid) {
			dispatch({ type: 'SET_FORM_SUBMITTED', payload: true });
			const formResponse = await handleSubmit(e);

			if (formResponse) {
				setSuccessAlert(true);

				trackingManager.trackFormSubmissionSuccessMessageLoad(
					{
						contentId: undefined,
					},
					getTrackingForm(formResponse),
				);
			} else {
				setErrorAlert(true);
			}
		} else {
			setInfoAlert(true);
		}
		setSending(false); // Comentar para deshabilitar otros envios despues de un Success
	}

	return (
		<>
			<CTA
				tag="button"
				type="submit"
				emphasis="primary"
				onClick={(e) => {
					e.preventDefault();
					handleClickSend(e);
				}}
				stretchContent={window.innerWidth <= 560}
				disabled={sending}
				icon={sending ? <Spinner variant="default" /> : null}
				iconPosition="end"
			>
				Enviar
			</CTA>
			{infoAlert ? <AlertInfo>Revisa los campos del formulario.</AlertInfo> : null}
			{errorAlert ? <AlertError>Se ha producido un error.</AlertError> : null}
			{successAlert ? <AlertSuccess>Datos enviados correctamente.</AlertSuccess> : null}
		</>
	);
}
